import React, { useState } from 'react';
import { Modal } from '../Modal';
import PhaseDetails from './PhaseDetails';
import { CareerFocusWithMetrics } from '../../hooks/useCareerFocus';
import { InternshipPhase } from './InternshipDashboardV2';
import { useDarkMode } from '../../context/DarkModeContext';
import { IoCloseOutline, IoChevronBackOutline, IoChevronForwardOutline } from 'react-icons/io5';
import { motion, AnimatePresence } from 'framer-motion';
import { usePhaseGuidanceContext } from '../../context/PhaseGuidanceContext';

interface PhaseDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPhase: InternshipPhase;
  focus: CareerFocusWithMetrics;
}

const phaseInfo: Record<InternshipPhase, { name: string }> = {
  onboarding: { name: 'Onboarding' },
  foundation: { name: 'Foundation' },
  growth: { name: 'Growth' },
  impact: { name: 'Impact' },
  transition: { name: 'Transition' }
};

const PhaseDetailsModal: React.FC<PhaseDetailsModalProps> = ({
  isOpen,
  onClose,
  currentPhase,
  focus
}) => {
  const { darkMode } = useDarkMode();
  const [selectedPhase, setSelectedPhase] = useState<InternshipPhase>(currentPhase);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const { allGuidance, loading } = usePhaseGuidanceContext();
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';

  // Reset to current phase when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedPhase(currentPhase);
    }
  }, [isOpen, currentPhase]);

  const phases: InternshipPhase[] = ['onboarding', 'foundation', 'growth', 'impact', 'transition'];
  const currentIndex = phases.indexOf(selectedPhase);
  const hasNext = currentIndex < phases.length - 1;
  const hasPrev = currentIndex > 0;

  const goToNext = () => {
    if (hasNext) {
      setSlideDirection('left');
      setSelectedPhase(phases[currentIndex + 1]);
    }
  };

  const goToPrev = () => {
    if (hasPrev) {
      setSlideDirection('right');
      setSelectedPhase(phases[currentIndex - 1]);
    }
  };

  const selectedInfo = phaseInfo[selectedPhase];
  const isCurrentPhase = selectedPhase === currentPhase;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={`${bgColor} rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b ${borderColor}`}>
          <div className="flex items-center justify-between">
            {/* Previous Button */}
            <button
              onClick={goToPrev}
              disabled={!hasPrev}
              className={`p-2 rounded-lg transition-colors ${
                hasPrev 
                  ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300' 
                  : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
              }`}
              aria-label="Previous phase"
            >
              <IoChevronBackOutline className="w-5 h-5" />
            </button>
            
            {/* Title - centered with flex-1 */}
            <div className="flex-1 text-center">
              <h2 className={`text-xl font-semibold ${textColor}`}>
                {selectedInfo.name} Phase
                {isCurrentPhase && (
                  <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                    Current
                  </span>
                )}
              </h2>
            </div>

            {/* Right side buttons container with fixed width */}
            <div className="flex items-center gap-2">
              {/* Next Button */}
              <button
                onClick={goToNext}
                disabled={!hasNext}
                className={`p-2 rounded-lg transition-colors ${
                  hasNext 
                    ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300' 
                    : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                }`}
                aria-label="Next phase"
              >
                <IoChevronForwardOutline className="w-5 h-5" />
              </button>

              {/* Close Button */}
              <button
                onClick={onClose}
                className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
                aria-label="Close modal"
              >
                <IoCloseOutline className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedPhase}
              initial={{ opacity: 0, x: slideDirection === 'left' ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: slideDirection === 'left' ? -50 : 50 }}
              transition={{ duration: 0.2 }}
            >
              <PhaseDetails 
                currentPhase={selectedPhase} 
                guidance={allGuidance?.[selectedPhase] || null}
                loading={loading}
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Modal>
  );
};

export default PhaseDetailsModal;